import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { verifyEmail, resendVerification } from "../../lib/authApi";
import { ApiError } from "../../lib/api";
import Wordmark from "../../components/Brand/Wordmark";

type VerifyState = "working" | "done" | "invalid";

/*
 * Velaigal /verify — the email-link target from the verify_email send.
 * On mount, reads ?token= and calls verifyEmail(token). Three states:
 *   working — verifying, on-brand loader (label + rule, no spinner icon)
 *   done    — success, sign-in CTA
 *   invalid — missing/expired/used token, with a resend-email fallback
 * so a stale or already-consumed link isn't a dead end.
 */
const VerifyEmail: React.FC = () => {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") ?? "";

  const [state, setState] = useState<VerifyState>("working");
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);

  // Guards the mutation itself, not just the resulting setState. StrictMode
  // double-invokes this effect in dev; the token is single-use, so a naive
  // "cancelled" flag on the setState still lets request #1 consume the
  // token, then request #2 hits an already-used token and reports
  // invalid_token even though verification succeeded. firedRef is not
  // reset by the cleanup, so only the first of the two mounts fires the
  // network call — the second mount's effect sees firedRef.current === true
  // and returns immediately, safe because the component instance (and its
  // state) survives StrictMode's remount.
  const firedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    if (firedRef.current) return;
    firedRef.current = true;
    verifyEmail(token)
      .then(() => setState("done"))
      .catch(() => setState("invalid"));
  }, [token]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resending) return;
    if (!email) {
      toast.error("Enter your email first.");
      return;
    }
    setResending(true);
    try {
      const res = await resendVerification(email);
      toast.success(res.message);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not send verification email.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-cream">
      {/* Left half — editorial framing, matches LoginPage's wrapper. */}
      <aside className="relative bg-cream px-8 py-12 lg:px-16 lg:py-20 flex flex-col justify-between">
        <span
          aria-hidden="true"
          className="hidden lg:block absolute top-8 right-10 select-none pointer-events-none"
          style={{
            fontFamily: '"Noto Serif Tamil", Georgia, serif',
            fontSize: "72px",
            fontWeight: 500,
            color: "var(--color-ink)",
            opacity: 0.18,
            lineHeight: 1,
          }}
        >
          வே
        </span>

        <Link
          to="/"
          aria-label="Velaigal home"
          className="text-ink no-underline self-start relative z-10"
        >
          <Wordmark variant="lockup" size="md" animateOnMount />
        </Link>

        <div className="lg:max-w-md mt-12 lg:mt-0 relative z-10">
          <h1
            className="font-display text-3xl lg:text-5xl font-medium text-ink leading-tight tracking-tight velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            Confirming your <span className="text-saffron">account</span>.
          </h1>
          <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-6" />
          <p className="mt-6 text-base lg:text-lg text-ink-secondary leading-relaxed">
            One quick check, and your account is ready to go.
          </p>
        </div>

        <span className="hidden lg:block text-[11px] uppercase tracking-widest text-ink-tertiary mt-12">
          Made for hiring teams and jobseekers across the World.
        </span>
      </aside>

      {/* Right half — state panel. */}
      <section className="bg-paper flex items-center justify-center px-6 py-12 lg:px-16 lg:py-20 border-t lg:border-t-0 lg:border-l border-hairline">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 text-center">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary">
              Verify email
            </span>
          </div>

          {state === "working" && (
            <div className="text-center">
              <Wordmark variant="wordmark" size="sm" />
              <span aria-hidden="true" className="velaigal-loader-rule mx-auto mt-4 w-12" />
              <p className="mt-6 text-sm text-ink-tertiary">Verifying your email.</p>
            </div>
          )}

          {state === "done" && (
            <div className="text-center space-y-6">
              <h2 className="font-display text-2xl font-medium text-ink tracking-tight">
                Email verified.
              </h2>
              <p className="text-sm text-ink-secondary leading-relaxed">
                Your account is ready. Sign in to pick up where you left off.
              </p>
              <Link to="/login" className="btn-primary w-full">
                Sign in
              </Link>
            </div>
          )}

          {state === "invalid" && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h2 className="font-display text-2xl font-medium text-ink tracking-tight">
                  Link invalid or expired.
                </h2>
                <p className="text-sm text-ink-secondary leading-relaxed">
                  Enter your email and we will send a fresh verification link.
                </p>
              </div>

              <form onSubmit={handleResend} className="space-y-5">
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-ink">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="input"
                  />
                </div>

                <button type="submit" disabled={resending} className="btn-primary w-full">
                  {resending ? "Sending…" : "Send new link"}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default VerifyEmail;
